import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getSurveyRecords } from '../services'
import { createSurveyListColumns } from '../components/Survey/List/columns'
import {
    getQuarterPeriod,
    SurveyFilters,
    SURVEY_FILTER_DEFAULTS,
} from '../utils/report-filters'
import { useSurveyFilterForm } from './use-survey-filter-form'
export type { SurveyFilters } from '../utils/report-filters'

export const useSurveyList = () => {
    const { t } = useTranslation('surveys')

    const {
        params,
        filters: urlFilters,
        queryParams,
        setParams,
        setFilters,
        searchInputProps,
    } = useUrlSearchParams<SurveyFilters>({
        defaults: { page_size: 10 },
        filterDefaults: SURVEY_FILTER_DEFAULTS,
    })

    const { control, filters } = useSurveyFilterForm(urlFilters, setFilters)

    const [searchParams] = useSearchParams()
    const period = useMemo(() => getQuarterPeriod(searchParams), [searchParams])
    const effectiveFilters = useMemo(
        () => ({
            ...filters,
            execution_time_range: period.execution_time_range,
        }),
        [filters, period.execution_time_range]
    )
    const effectiveQueryParams = useMemo(
        () => ({
            ...queryParams,
            filters: btoa(JSON.stringify(effectiveFilters)),
        }),
        [queryParams, effectiveFilters]
    )

    const { data, renderState, isFetching, isError } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SURVEYS_LIST, effectiveQueryParams],
        queryFn: () => getSurveyRecords(effectiveQueryParams),
        placeholderData: true,
    })
    const [isExporting, setIsExporting] = useState(false)

    const columns = useMemo(() => createSurveyListColumns(t), [t])

    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )

    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        surveys: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),
        isFetching,
        params,
        control,
        searchInputProps,
        columns,
        effectiveQueryParams,
        isError,
        isExporting,
        setIsExporting,
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        t,
    }
}
